/**
 * The games a brand's places run, editable (place manager, unit 8): the
 * brand's Games page (`/b/:brandId/games`). Every catalog game is a row with a
 * "runs here" tick; a listed slug the catalog does not have is a row too, kept
 * unless unticked. Ticks edit a draft; "Review" shows one line of only the
 * changes; "Apply" sends the full new list; "Undo" sends the list before back,
 * for five minutes. The write rules are the portal's (`writes.ts`).
 *
 * Today the list is per BRAND (`Brand.allowedGameSlugs`): every place of the
 * brand runs it, and each place's Games section (unit 5) follows on its next
 * read. A place's own list is not here.
 *
 * What the backend does with the list (`updateBrand`, any owner or member of
 * the brand, or staff): it replaces it wholesale; trims, drops blanks,
 * de-duplicates; never checks a slug against the catalog. An empty list means
 * the brand's app shows NO games (not "all"), so an Apply that leaves no
 * catalog game showing needs a second tap ({@link needsSecondTap}).
 *
 * `updateBrand` takes no expected version, so each Apply and Undo reads the
 * list just before sending and sends nothing when it changed since. That is
 * check-then-act: it narrows the window in which two members overwrite each
 * other's list unseen (a read and a write apart), it does not close it. Only
 * a precondition on the backend (an expected version on `updateBrand`) would;
 * that is a later ask.
 *
 * Pure, so the rows, the list sent, the review line and the write outcomes are
 * unit-testable; the reads and the write come in through {@link BrandGamesApi}.
 */
import type { GameCatalogData } from "@/data/place-games-api";
import { catalogBySlug, unknownWords, type Game } from "@/features/places/games";
import { failureLine, undoWindowOpen, type WriteGuards } from "@/features/writes/writes";
import { plural } from "@/lib/utils";

/** The brand's list and the catalog, as read. */
export interface BrandGamesRead {
  /** `Brand.allowedGameSlugs`: raw slugs, in the brand's order. */
  slugs: string[];
  /** The catalog: every game not archived for staff (`all`), the enabled ones for anyone else. */
  catalog: GameCatalogData;
}

/** One row of the page: a slug, and the catalog's game for it (null: a listed
 *  slug the catalog does not have). */
export interface GameChoice {
  slug: string;
  game: Game | null;
}

/** The catalog's games, in its order, each slug once ({@link catalogBySlug},
 *  joined once per catalog read). */
function catalogGames(catalog: GameCatalogData): Game[] {
  return [...catalogBySlug(catalog).values()];
}

/** The page's rows: every catalog game, in the catalog's order; then each
 *  listed slug the catalog does not have, in the list's order. */
export function choicesOf(read: BrandGamesRead): GameChoice[] {
  const games = catalogGames(read.catalog);
  const known = new Set(games.map((g) => g.slug));
  const unknown = [...new Set(read.slugs)].filter((slug) => !known.has(slug));
  return [...games.map((game) => ({ slug: game.slug, game })), ...unknown.map((slug) => ({ slug, game: null }))];
}

/** How many games of `slugs` show in the app, as the place's Games section
 *  counts them: the ones in the catalog and switched on. */
export function showingCount(slugs: readonly string[], catalog: GameCatalogData): number {
  const enabled = new Set(catalogGames(catalog).filter((g) => g.enabled).map((g) => g.slug));
  return new Set(slugs.filter((s) => enabled.has(s))).size;
}

/** What a listed slug the catalog does not have says on its row. */
export function unknownRowLine(catalog: GameCatalogData): string {
  return `${unknownWords(!catalog.all)}; kept unless you untick it`;
}

/** The line above the rows: "5 games show in this brand's app (+1 not an
 *  active catalog game · 1 disabled)."; an empty list says the backend's rule. */
export function headerLine(read: BrandGamesRead): string {
  if (read.slugs.length === 0) return "No games show in this brand's app.";
  const bySlug = new Map(catalogGames(read.catalog).map((g) => [g.slug, g]));
  const listed = [...new Set(read.slugs)];
  const unknown = listed.filter((s) => !bySlug.has(s)).length;
  const disabled = listed.filter((s) => bySlug.get(s)?.enabled === false).length;
  const n = showingCount(listed, read.catalog);
  const count = `${n} ${plural(n, "game")} ${n === 1 ? "shows" : "show"} in this brand's app`;
  const aside = [unknown > 0 && `+${unknown} ${unknownWords(!read.catalog.all)}`, disabled > 0 && `${disabled} disabled`]
    .filter((p): p is string => !!p)
    .join(" · ");
  return aside ? `${count} (${aside}).` : `${count}.`;
}

/** The list Apply sends for `ticked`: the catalog's games ticked, in the
 *  catalog's order, with each listed slug the catalog does not have kept in
 *  its place (unless unticked): right after the slug it followed in the list
 *  as read, the nearest one still sent; first when none is. */
export function listFor(read: BrandGamesRead, ticked: ReadonlySet<string>): string[] {
  const games = catalogGames(read.catalog);
  const known = new Set(games.map((g) => g.slug));
  const list = games.filter((g) => ticked.has(g.slug)).map((g) => g.slug);
  read.slugs.forEach((slug, i) => {
    if (known.has(slug) || !ticked.has(slug) || list.includes(slug)) return;
    const before = read.slugs.slice(0, i).findLast((s) => list.includes(s));
    list.splice(before === undefined ? 0 : list.indexOf(before) + 1, 0, slug);
  });
  return list;
}

/** A change to the list: what it adds (catalog order) and removes (the list's
 *  order), the list before and the list sent, and whether the games kept go
 *  in another order (the list sent is in the catalog's). */
export interface GamesChange {
  adds: GameChoice[];
  removes: GameChoice[];
  before: string[];
  after: string[];
  reordered: boolean;
}

/** The change `ticked` makes to the list as read; null when it adds and
 *  removes nothing (nothing to review). */
export function changeOf(read: BrandGamesRead, ticked: ReadonlySet<string>): GamesChange | null {
  const listed = new Set(read.slugs);
  const choices = choicesOf(read);
  const adds = choices.filter((c) => ticked.has(c.slug) && !listed.has(c.slug));
  const removes = [...listed].filter((s) => !ticked.has(s)).map((slug) => choices.find((c) => c.slug === slug) ?? { slug, game: null });
  if (adds.length === 0 && removes.length === 0) return null;
  const after = listFor(read, ticked);
  const keptBefore = [...listed].filter((s) => ticked.has(s));
  const keptAfter = after.filter((s) => listed.has(s));
  return {
    adds,
    removes,
    before: [...read.slugs],
    after,
    reordered: keptAfter.some((s, i) => s !== keptBefore[i]),
  };
}

/** What the review line says when the list sent is empty. */
export const NO_GAMES_LINE = "No games will show in the app";

/** What it says when the list sent keeps only games switched off or not in
 *  the catalog (a slug the catalog lacks may still name a game the app has). */
export const NO_CATALOG_GAMES_LINE = "No game from the catalog will show in the app";

/** Whether Apply needs a second tap: no catalog game switched on is left, so
 *  the brand's app will show no games (the list is empty) or none of the
 *  catalog's. */
export function needsSecondTap(change: GamesChange, catalog: GameCatalogData): boolean {
  return showingCount(change.after, catalog) === 0;
}

const names = (choices: readonly GameChoice[]) => choices.map((c) => c.game?.name ?? c.slug).join(", ");

/** The one line "Review" shows: only the changes, then how many games will
 *  show ("Adds Hearts, Spades · removes Crossword · 6 games will show"); the
 *  order when it changes; {@link NO_GAMES_LINE} when the list sent is empty,
 *  {@link NO_CATALOG_GAMES_LINE} when no catalog game is left. */
export function reviewLine(change: GamesChange, catalog: GameCatalogData): string {
  const n = showingCount(change.after, catalog);
  const line = [
    change.adds.length > 0 && `adds ${names(change.adds)}`,
    change.removes.length > 0 && `removes ${names(change.removes)}`,
    change.reordered && "puts the list in the catalog's order",
    change.after.length === 0 ? NO_GAMES_LINE : n === 0 ? NO_CATALOG_GAMES_LINE : `${n} ${plural(n, "game")} will show`,
  ]
    .filter((p): p is string => !!p)
    .join(" · ");
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/** The read and the write (`brands-api.ts`, bound to the brand and viewer). */
export interface BrandGamesApi {
  /** The brand's list and the catalog; null when the brand is not visible. */
  read(): Promise<BrandGamesRead | null>;
  /** Replace the brand's list with `slugs`. */
  write(slugs: string[]): Promise<void>;
}

/** A change applied here, and where its undo stands: ready; going; undone;
 *  failed (Undo stays: sending the list before again is safe, it replaces the
 *  whole list); held (the list changed since, or a later Apply's answer was
 *  lost: no Undo from here); or gone (the brand is no longer readable here). */
export interface GamesChanged {
  before: string[];
  after: string[];
  /** When it was applied (ms since epoch): "Undo" lasts five minutes from then. */
  madeAt: number;
  undo: "ready" | "undoing" | "undone" | { failed: string } | { held: string } | { gone: string };
}

/** How an Apply went: applied; refused (in plain words; Apply stays); its
 *  answer lost (it may have gone through: no second Apply from that review);
 *  not sent, the list having changed since it was read (read again, the
 *  change to review once more); or not sent, the brand no longer readable
 *  here (read again: the page then says so, nothing to review). */
export type ApplyOutcome = { changed: GamesChanged } | { failed: string } | { lost: string } | { stale: string } | { gone: string };

/** What a write not sent because the brand no longer reads here says (deleted,
 *  or the viewer's access to a draft brand removed). */
export const GONE_LINE = "This brand is no longer yours to change, or it is gone: nothing was sent.";

/** What an earlier change's Undo says once a later Apply's answer was lost (that
 *  Apply may have gone through: this Undo would take it back too, unseen). */
export const LATER_CHANGE_LINE = "A later change may have gone through; tick the games to put them as you want them.";

/** What an Apply not sent because the list changed since says. */
export const STALE_LINE =
  "The brand's games changed since they were read here, so nothing was sent. They are read again: review the change once more.";

/** What an Undo not sent because the list changed since the change says. */
export const UNDO_HELD_LINE =
  "The brand's games changed again since this change, so Undo would overwrite that: nothing was sent. Tick the games to put them as you want them.";

/** Whether two lists are the same, slug for slug, in order. */
function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

/** The list as stored now, read just before a write; null when the brand is not visible. */
async function listNow(api: Pick<BrandGamesApi, "read">): Promise<string[] | null> {
  return (await api.read())?.slugs ?? null;
}

/** What an Apply whose answer never came says. */
export const APPLY_LOST_LINE =
  "Could not hear back from pop.in, so the change may have gone through. The brand's games are read again now, in a few seconds and in half a minute: check them before changing them again.";

/** Apply the change: read the list, and when it is still the one the change
 *  was worked out from, send the full new list. A lost answer reads the brand
 *  again (`guards.reread`). Never throws. */
export async function applyChange(
  change: GamesChange,
  api: BrandGamesApi,
  guards: WriteGuards,
  clock: () => number = Date.now,
): Promise<ApplyOutcome> {
  let now: string[] | null;
  try {
    now = await listNow(api);
  } catch (e) {
    console.warn("Could not read the games before changing them:", e);
    return { failed: failureLine(e, "read the games") };
  }
  if (!now) return { gone: GONE_LINE };
  if (!sameList(now, change.before)) return { stale: STALE_LINE };
  try {
    await api.write(change.after);
  } catch (e) {
    console.warn("Could not change the games:", e);
    if (!guards.isLost(e)) return { failed: failureLine(e, "change the games") };
    guards.reread();
    return { lost: APPLY_LOST_LINE };
  }
  return { changed: { before: change.before, after: change.after, madeAt: clock(), undo: "ready" } };
}

/** Undo: read the list, and when it is still the one the change left, send
 *  the list before back; how it went (never throws). Already back to the list
 *  before (an earlier Undo whose answer was lost went through): undone.
 *  Changed otherwise: held, no Undo again (it would overwrite that). A lost
 *  answer reads the brand again and keeps Undo (sending it again is safe). */
export async function undoGamesChange(
  changed: GamesChanged,
  api: BrandGamesApi,
  guards: WriteGuards,
  now: number,
): Promise<GamesChanged["undo"]> {
  if (!undoWindowOpen(changed, now)) return { failed: "The five minutes to undo have passed. Tick the games again to put them back." };
  let current: string[] | null;
  try {
    current = await listNow(api);
  } catch (e) {
    console.warn("Could not read the games before undoing:", e);
    return { failed: failureLine(e, "read the games") };
  }
  if (!current) return { gone: GONE_LINE };
  if (sameList(current, changed.before)) return "undone";
  if (!sameList(current, changed.after)) return { held: UNDO_HELD_LINE };
  try {
    await api.write(changed.before);
    return "undone";
  } catch (e) {
    console.warn("Could not undo the games change:", e);
    if (guards.isLost(e)) guards.reread();
    return { failed: failureLine(e, "undo it") };
  }
}

/** The line for a change applied here: what was done, and that each place
 *  follows; or that it was undone. */
export function changedLine(changed: GamesChanged): string {
  if (typeof changed.undo === "object" && "held" in changed.undo) return changed.undo.held;
  if (typeof changed.undo === "object" && "gone" in changed.undo) return changed.undo.gone;
  if (changed.undo === "undone") return "Undone: the brand's games are as they were. Each place's Games section follows on its next open.";
  return "Changed: the games this brand's places run. Each place's Games section follows on its next open.";
}

/** Who may see and change the list: the brand's owner and members, and pop.in
 *  staff (as `updateBrand` allows). Anyone else (a signed-in partner who
 *  reached an active brand that is not theirs) gets no page and no link. */
export function canEditBrandGames(viewer: { isSuperAdmin: boolean; viewerRole?: "OWNER" | "MEMBER" | null }): boolean {
  return viewer.isSuperAdmin || viewer.viewerRole === "OWNER" || viewer.viewerRole === "MEMBER";
}
